import { useCallback, useEffect, useState } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { renderRoutes } from 'react-router-config';
import RoutesConfig from '../router';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import { CommonAction, ReduxCommonActionType } from '../store/modules/common';
import { getUserInfo } from '../api';
import { isSupportSystem, setConfig } from '../config';
import { aegis, initAegis } from '../lib';
import {
  intiLoginStep,
  isNeedJoinStudio,
  joinStudioApi,
  redirectRemoveSecurity,
} from './lib';
import { initializationCrontab } from '../lib/crontab/initialization';
import { initIoaLogin } from './admin/lib/ioa-helper';
import { WindowEvent } from '../model';
import {
  fetchUserPermission,
  PermissionAction,
} from '../store/modules/permission';
import { IRootState } from '../store/reducers';

const Root = () => {
  const userInfo = useSelector((state: IRootState) => state.common.userInfo);
  const commonDispatch = useDispatch<Dispatch<CommonAction>>();
  const permissionDispatch = useDispatch<Dispatch<PermissionAction>>();
  const [initLoading, setInitLoading] = useState<boolean>(false);

  useEffect(() => {
    if (isSupportSystem()) {
      initSupport();
      return;
    }
    initialization();
  }, []);

  const checkUserChange = useCallback(() => {
    getUserInfo(true).then((res) => {
      if (res?.UserID && userInfo?.UserID !== res?.UserID) {
        window.location.reload();
      }
    });
  }, [userInfo?.UserID]);

  useEffect(() => {
    if (!userInfo?.UserID) {
      return;
    }

    window.addEventListener('focus', checkUserChange);

    return () => {
      window.removeEventListener('focus', checkUserChange);
    };
  }, [checkUserChange]);

  const initSupport = async () => {
    if (!isSupportSystem()) {
      return;
    }

    const res = await initIoaLogin();
    if (!res) {
      return;
    }
    const userInfo = await getLoginUserInfo();

    initializationCrontab();
    initAegis(userInfo?.UserID);
    setInitLoading(!!userInfo);
  };

  const initialization = async () => {
    if (
      location.pathname.startsWith('/doc-public/detail') ||
      location.pathname.startsWith('/doc/detail')
    ) {
      setInitLoading(true);
      return;
    }
    const loginStep = await intiLoginStep();
    if (!loginStep) {
      return;
    }

    // old user join studio from mail open or new user first login check join scenes
    const inviteKey = isNeedJoinStudio();
    // console.log('inviteKey获得:', inviteKey);
    if (inviteKey) {
      await joinStudioApi(inviteKey);
      redirectRemoveSecurity();
    }

    // first login, replace all search to empty
    if (loginStep === 1) {
      redirectRemoveSecurity();
      return;
    }

    const res = await getLoginUserInfo();
    initializationCrontab();
    initAegis(res?.UserID);
    setInitLoading(!!res);
  };

  const permissionChangeHandler = () => {
    fetchUserPermission(permissionDispatch)();
  };

  useEffect(() => {
    window.addEventListener(
      WindowEvent.PermissionChange,
      permissionChangeHandler
    );

    return () => {
      window.removeEventListener(
        WindowEvent.PermissionChange,
        permissionChangeHandler
      );
    };
  }, []);

  const getLoginUserInfo = async () => {
    const res = await getUserInfo();
    if (res) {
      setConfig({ userID: res.UserID });
      aegis.setConfig('uin', res.UserID);
      commonDispatch({
        type: ReduxCommonActionType.LOGIN,
        result: res,
      });
      return res;
    }
    return null;
  };

  const renderRouter = () => {
    if (initLoading) {
      return (
        <div className="u-page-wrapper">
          <div className="gc-page">{renderRoutes(RoutesConfig)}</div>
        </div>
      );
    }
    return null;
  };

  return <BrowserRouter>{renderRouter()}</BrowserRouter>;
};

export default Root;
